import jsPDF from 'jspdf';

/**
 * Convierte un elemento SVG o imagen a una URL de datos (data URL)
 * @param element - Elemento SVG o imagen a convertir
 * @param type - Tipo de elemento ('svg' o 'img')
 * @returns Promise con la URL de datos
 */
export const imageToDataUrl = (element: Element, type: 'svg' | 'img' = 'svg'): Promise<string> => {
  // Crear un canvas temporal
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo obtener el contexto 2D');

  // Configurar el tamaño del canvas (optimizado para el tamaño final del PDF)
  canvas.width = 400; // ~35mm a 300dpi
  canvas.height = 400;

  let url: string;
  if (type === 'svg') {
    const svgElement = element as SVGElement;
    // Convertir el SVG a una imagen
    const svgData = new XMLSerializer().serializeToString(svgElement);

    // Asegurarnos de que el SVG tenga dimensiones
    if (!svgElement.hasAttribute('width')) svgElement.setAttribute('width', '1000');
    if (!svgElement.hasAttribute('height')) svgElement.setAttribute('height', '1000');
    if (!svgElement.hasAttribute('viewBox')) svgElement.setAttribute('viewBox', '0 0 1000 1000');

    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    url = URL.createObjectURL(svgBlob);
  } else {
    const imgElement = element as HTMLImageElement;
    url = imgElement.src;
  }

  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        // Limpiar el canvas
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Dibujar la imagen centrada
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const x = (canvas.width - img.width * scale) / 2;
        const y = (canvas.height - img.height * scale) / 2;

        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

        // Convertir a PNG con compresión
        const dataUrl = canvas.toDataURL('image/png', 0.8);
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error al convertir SVG a imagen'));
    };
    img.src = url;
  });
};

/**
 * Genera un PDF individual para un código QR usando jsPDF
 * @param qrElement - Elemento DOM que contiene el QR y la información
 * @returns Promise con el Blob del PDF generado
 */
export const generateIndividualQrPDF = async (qrElement: Element): Promise<Blob> => {
  try {
    // Obtener el logo
    const logoImg = qrElement.querySelector('.logoContainer img') as HTMLImageElement;
    if (!logoImg) throw new Error('No se encontró el logo');
    const logoData = await imageToDataUrl(logoImg, 'img');

    // Obtener el SVG del QR
    const svgElement = qrElement.querySelector('svg');
    if (!svgElement || !(svgElement instanceof SVGElement)) {
      throw new Error('No se encontró el elemento SVG del QR o no es un SVG válido');
    }

    // Convertir SVG a imagen base64
    const qrImageData = await imageToDataUrl(svgElement, 'svg');

    // Crear el PDF con compresión
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [75, 50.5],
      compress: true,
      putOnlyUsedFonts: true,
      precision: 2
    });

    // Obtener los textos
    const serialElement = qrElement.querySelector('.serialText');
    const orgNameElement = qrElement.querySelector('.printableTicketInfoText');
    const productIdElement = qrElement.querySelector('.printableTicketInfoText:last-child');

    // Calcular dimensiones y posiciones
    const pageWidth = 75; // ancho de página en mm
    const logoHeight = 10; // altura del logo en mm
    const qrSize = 45; // tamaño del QR en mm
    const textMargin = 2; // espacio entre elementos en mm
    const leftMargin = 4; // margen izquierdo en mm
    const centerX = pageWidth / 2;

    // Agregar borde al PDF
    pdf.setDrawColor(0, 0, 0); // Negro
    pdf.setLineWidth(0.8); // Grosor del borde aumentado
    pdf.rect(0, 0, pageWidth, 297); // Alto de una página A4 en mm

    // Posicionar elementos verticalmente
    let currentY = 1; // Reducido el espacio superior

    // Agregar el logo centrado
    const contentCenterX = centerX - 12; // Mover todo 12mm a la izquierda
    const logoX = contentCenterX - 5; // 10mm de ancho
    pdf.addImage(logoData, 'PNG', logoX, currentY, 10, logoHeight, undefined, 'FAST');
    currentY += logoHeight - 3; // Superponemos 5mm con el QR

    // Agregar el QR centrado
    const qrX = contentCenterX - qrSize / 2;
    pdf.addImage(qrImageData, 'PNG', qrX, currentY, qrSize, qrSize, undefined, 'FAST');
    currentY += qrSize + textMargin;

    // Configurar fuente
    pdf.setFontSize(9);

    // Agregar los textos
    if (serialElement) {
      const serialText = serialElement.textContent?.replace('Serial:', '').trim() || '';
      // Serial con título y valor en la misma línea
      pdf.setFont('Helvetica', 'normal');
      const serialLabel = 'Serial: ';
      const titleWidth = pdf.getTextWidth(serialLabel);
      const serialValueWidth = pdf.getTextWidth(serialText);

      // Calcular el ancho disponible basado en la posición del QR
      const qrRightEdge = contentCenterX + qrSize / 2 - 3; // Borde derecho del QR con margen ajustado
      const maxLineWidth = qrRightEdge - (leftMargin + 2); // Usar el mismo ancho que ocupa el QR

      // Para la primera línea, restar el espacio del título (reducido el espacio entre título y valor)
      const firstLineWidth = maxLineWidth - (titleWidth + 1);
      // Para las líneas siguientes, usar todo el ancho disponible
      const subsequentLinesWidth = maxLineWidth;

      // Calcular si el texto completo cabe en una línea
      const totalWidth = titleWidth + 2 + serialValueWidth;

      if (totalWidth > maxLineWidth) {
        // Dividir el texto en dos partes
        const firstPart = pdf.splitTextToSize(serialText, firstLineWidth)[0];
        const remainingText = serialText.slice(firstPart.length);
        // Dividir el texto restante usando todo el ancho disponible
        const remainingLines = remainingText ? pdf.splitTextToSize(remainingText, subsequentLinesWidth) : [];

        // Poner "Serial:" y la primera parte del valor al mismo nivel
        pdf.text(serialLabel, leftMargin + 2, currentY);
        pdf.setFont('Helvetica', 'bold');
        pdf.text(firstPart, leftMargin + 2 + titleWidth + -0.5, currentY);

        // Si hay texto restante, ponerlo debajo alineado con "Serial:"
        if (remainingLines.length > 0) {
          currentY += 3;
          pdf.text(remainingLines, leftMargin + 2, currentY);
        }
      } else {
        // Si cabe todo en una línea, mostrar junto
        pdf.text(serialLabel, leftMargin + 2, currentY);
        pdf.setFont('Helvetica', 'bold');
        pdf.text(serialText, leftMargin + 2 + titleWidth + 1, currentY);
      }
      // Ajustar el espacio vertical según si hay múltiples líneas
      currentY += 1.5;
    }

    // Línea separadora centrada
    pdf.setDrawColor(198, 199, 203); // #C6C7CB
    pdf.setLineWidth(0.2); // Línea más delgada
    pdf.line(contentCenterX - 20, currentY, contentCenterX + 20, currentY);
    pdf.setLineWidth(0.8); // Restaurar grosor para el borde
    currentY += 4;

    // Información adicional centrada
    if (orgNameElement) {
      const orgText = orgNameElement.textContent?.replace('Empresa:', '').trim() || '';
      // Título y valor en la misma línea
      const text = `Empresa: ${orgText}`;
      pdf.setFont('Helvetica', 'normal');
      const titleWidth = pdf.getTextWidth('Empresa: ');
      const startX = contentCenterX - pdf.getTextWidth(text) / 2;
      pdf.text('Empresa: ', startX, currentY);
      pdf.setFont('Helvetica', 'bold');
      pdf.text(orgText, startX + titleWidth, currentY);
      currentY += 4;
    }

    if (productIdElement) {
      const productText = productIdElement.textContent?.replace('Product ID:', '').trim() || '';
      // Título y valor en la misma línea
      const text = `Product ID: ${productText}`;
      pdf.setFont('Helvetica', 'normal');
      const titleWidth = pdf.getTextWidth('Product ID: ');
      const startX = contentCenterX - pdf.getTextWidth(text) / 2;
      pdf.text('Product ID: ', startX, currentY);
      pdf.setFont('Helvetica', 'bold');
      pdf.text(productText, startX + titleWidth, currentY);
      currentY += 3;
    }

    // Convertir a Blob
    return pdf.output('blob');
  } catch (error) {
    throw error;
  }
};

/**
 * Descarga un PDF generado como archivo
 * @param pdfBlob - Blob del PDF a descargar
 * @param filename - Nombre del archivo para la descarga
 */
export const downloadPDF = (pdfBlob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
